import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { register, validarInvitacion } from '../services/api';
import axios from 'axios';
import './Login.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function Register({ onLogin }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite') || '';

  const [formData, setFormData] = useState({
    email: '', password: '', nombre: '', apellidos: '', piso: '', telefono: '', rol: 'vecino'
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Estado de la invitación
  const [invitacion, setInvitacion] = useState(null);
  const [invError, setInvError] = useState('');
  const [invLoading, setInvLoading] = useState(false);

  // Búsqueda difusa de comunidad
  const [comunidadQuery, setComunidadQuery] = useState('');
  const [comunidadId, setComunidadId] = useState(null);
  const [comunidadNombre, setComunidadNombre] = useState('');
  const [sugerencias, setSugerencias] = useState([]);
  const [buscando, setBuscando] = useState(false);

  // Búsqueda difusa con debounce
  useEffect(() => {
    if (invitacion || comunidadId) return;
    if (!comunidadQuery || comunidadQuery.length < 2) { setSugerencias([]); return; }
    const timer = setTimeout(() => {
      setBuscando(true);
      axios.get(`${API_URL}/comunidades/buscar?q=${encodeURIComponent(comunidadQuery)}`)
        .then(res => setSugerencias(res.data))
        .catch(() => setSugerencias([]))
        .finally(() => setBuscando(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [comunidadQuery, invitacion, comunidadId]);

  // Validar el token al cargar la página si existe
  useEffect(() => {
    if (!inviteToken) return;
    setInvLoading(true);
    validarInvitacion(inviteToken)
      .then(res => {
        setInvitacion(res.data);
        if (res.data.email_restringido) {
          setFormData(fd => ({ ...fd, email: res.data.email_restringido }));
        }
      })
      .catch(err => {
        setInvError(err.response?.data?.error || 'Link de invitación no válido o caducado');
      })
      .finally(() => setInvLoading(false));
  }, [inviteToken]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSeleccionarComunidad = (c) => {
    setComunidadId(c.id);
    setComunidadNombre(c.nombre);
    setComunidadQuery(c.nombre);
    setSugerencias([]);
  };

  const handleComunidadQueryChange = (e) => {
    setComunidadQuery(e.target.value);
    setComunidadId(null);
    setComunidadNombre('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (inviteToken && !invitacion) {
      setError('El link de invitación no es válido. No puedes registrarte sin uno válido.');
      return;
    }
    if (!invitacion && !comunidadId) {
      setError('Debes seleccionar una comunidad válida.');
      return;
    }
    setLoading(true);
    try {
      const payload = { ...formData };
      if (inviteToken) payload.invite_token = inviteToken;
      if (!inviteToken && comunidadId) payload.comunidad_id = comunidadId;
      const response = await register(payload);
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
      }
      localStorage.setItem('user', JSON.stringify(response.data.user));
      onLogin(response.data.user);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Error al registrarse');
    } finally {
      setLoading(false);
    }
  };

  // Si hay token pero no es válido, mostramos error bloqueante
  if (inviteToken && invLoading) {
    return (
      <div className="login-root">
        <div className="login-panel-right">
          <div className="login-form-wrap">
            <p style={{ textAlign: 'center', color: '#aaa' }}>Validando invitación...</p>
          </div>
        </div>
      </div>
    );
  }

  if (inviteToken && invError) {
    return (
      <div className="login-root">
        <div className="login-panel-right">
          <div className="login-form-wrap">
            <div className="login-form-header">
              <h1 className="login-form-title">Invitación no válida</h1>
            </div>
            <div className="alert alert-error">{invError}</div>
            <p style={{ textAlign: 'center', marginTop: '1rem', color: '#8B85A7' }}>
              Solicita un nuevo link a tu administrador o presidente de comunidad.
            </p>
            <p style={{ textAlign: 'center', marginTop: '1rem' }}>
              <Link to="/login">Volver al inicio de sesión</Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-root">
      <div className="login-panel-right">
        <div className="login-form-wrap" style={{ maxWidth: '520px' }}>
          <div className="login-form-header">
            <h1 className="login-form-title">Crear Cuenta</h1>
            {invitacion ? (
              <p className="login-form-sub">Te has unido a <strong>{invitacion.comunidad_nombre}</strong></p>
            ) : (
              <p className="login-form-sub">Regístrate en tu comunidad</p>
            )}
          </div>

        {/* Banner de comunidad asignada */}
        {invitacion && (
          <div style={{
            background: 'linear-gradient(135deg, #667eea22, #764ba222)',
            border: '1px solid #667eea55',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <span style={{ fontSize: '1.4rem' }}>🏢</span>
            <div>
              <div style={{ fontWeight: '600', color: '#667eea' }}>{invitacion.comunidad_nombre}</div>
              <div style={{ fontSize: '0.82rem', color: '#aaa' }}>Comunidad asignada automáticamente</div>
            </div>
          </div>
        )}

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="grid grid-2">
            <div className="form-group">
              <label className="form-label">Nombre *</label>
              <input type="text" name="nombre" className="form-control" value={formData.nombre} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label className="form-label">Apellidos</label>
              <input type="text" name="apellidos" className="form-control" value={formData.apellidos} onChange={handleChange} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Email *</label>
            <input
              type="email"
              name="email"
              className="form-control"
              value={formData.email}
              onChange={handleChange}
              required
              readOnly={!!invitacion?.email_restringido}
              style={invitacion?.email_restringido ? { opacity: 0.7, cursor: 'not-allowed' } : {}}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Contraseña *</label>
            <input type="password" name="password" className="form-control" value={formData.password} onChange={handleChange} required />
          </div>
          <div className="grid grid-2">
            <div className="form-group">
              <label className="form-label">Piso</label>
              <input type="text" name="piso" className="form-control" value={formData.piso} onChange={handleChange} placeholder="1º A" />
            </div>
            <div className="form-group">
              <label className="form-label">Teléfono</label>
              <input type="tel" name="telefono" className="form-control" value={formData.telefono} onChange={handleChange} />
            </div>
          </div>
          {!invitacion && (
            <div className="form-group" style={{ position: 'relative' }}>
              <label className="form-label">Comunidad *</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Escribe el nombre de tu comunidad..."
                  value={comunidadQuery}
                  onChange={handleComunidadQueryChange}
                  autoComplete="off"
                  style={comunidadId ? { borderColor: '#22c55e', paddingRight: '2.2rem' } : {}}
                />
                {comunidadId && (
                  <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#22c55e', fontSize: '1.1rem' }}>✓</span>
                )}
                {buscando && (
                  <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#aaa', fontSize: '0.8rem' }}>...</span>
                )}
              </div>
              {sugerencias.length > 0 && !comunidadId && (
                <ul style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                  background: '#ffffff', border: '1px solid #d1d5db', borderRadius: '8px',
                  listStyle: 'none', margin: '4px 0 0', padding: '4px 0', boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
                }}>
                  {sugerencias.map(c => (
                    <li
                      key={c.id}
                      onClick={() => handleSeleccionarComunidad(c)}
                      style={{
                        padding: '10px 14px', cursor: 'pointer', fontSize: '0.9rem', color: '#1f2937',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f3f4f6'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <span>{c.nombre}</span>
                      {c.score < 0.85 && (
                        <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>¿Quisiste decir esto?</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {comunidadQuery.length >= 2 && !buscando && sugerencias.length === 0 && !comunidadId && (
                <p style={{ fontSize: '0.8rem', color: '#f87171', marginTop: '4px' }}>
                  No se encontró ninguna comunidad con ese nombre.
                </p>
              )}
              {comunidadId && (
                <p style={{ fontSize: '0.8rem', color: '#22c55e', marginTop: '4px' }}>
                  Comunidad asignada: <strong>{comunidadNombre}</strong>
                </p>
              )}
            </div>
          )}
          <button type="submit" className="btn btn-primary btn-lg" disabled={loading} style={{ width: '100%' }}>
            {loading ? 'Registrando...' : 'Registrarse'}
          </button>
        </form>
          <p style={{ textAlign: 'center', marginTop: '20px', fontSize: '14px', color: '#8B85A7' }}>
            ¿Ya tienes cuenta? <Link to="/login">Inicia sesión</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Register;
